import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RelativeTime } from '@/components/RelativeTime';

/**
 * Shows a freshly-issued provisioning token.
 *
 * Extracted from the onboarding success dialog so that both places an MSP can
 * mint a token — onboarding a new customer, and issuing another for an existing
 * one — render it through one component rather than two that drift.
 *
 * Presentational only: it makes no requests and holds no token state, so the
 * pages that use it decide when a token exists and when it stops being shown.
 *
 * Note the token is not a create-only secret on this platform — the tokens list
 * endpoint returns it too, and the Gateways page renders it in a table. Nothing
 * here should be read as the token's only appearance.
 */
export interface ProvisioningTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The raw token string. */
  token: string;
  tokenName?: string | null;
  /** When the token stops working. The server decides this, so it is shown, not computed. */
  expiresAt?: string | null;
  /** Anything the caller wants above the token — what was created, who was invited. */
  children?: ReactNode;
}

export function ProvisioningTokenDialog({
  open,
  onOpenChange,
  title,
  token,
  tokenName,
  expiresAt,
  children,
}: ProvisioningTokenDialogProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(token).then(
      () => {
        setCopied(true);
        window.setTimeout(() => { setCopied(false); }, 2000);
      },
      () => {
        // Clipboard access can be refused outright. The token is on screen and
        // selectable either way, so there is nothing to recover from — just
        // don't claim a copy that didn't happen.
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {children}

          <div>
            <h4 className="text-sm font-semibold mb-2">Provisioning token</h4>
            <div className="rounded-md border border-sev-5/30 bg-sev-5/5 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <code className="font-mono text-xs break-all flex-1" data-testid="provisioning-token">
                  {token}
                </code>
                <Button variant="outline" size="sm" onClick={copy} className="shrink-0 gap-1.5">
                  {copied ? <Check className="w-3 h-3" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              {tokenName ? (
                <p className="text-xs">
                  <strong>Name:</strong> {tokenName}
                </p>
              ) : null}
              {expiresAt ? (
                <p className="text-xs text-muted-foreground">
                  Expires <RelativeTime value={expiresAt} absolute className="text-xs" />
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Save this token — it is needed to provision gateways for this customer.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => { onOpenChange(false); }}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Alerts and lists an onboarding flow shows above the token. */
export function ProvisioningTokenDialogNotice({ children }: { children: ReactNode }) {
  return (
    <Alert className="border-sev-4/40 bg-sev-4/10">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
